// src/pages/sow-draft/SowDraftPage.jsx
//
// Draft SOW: a 3-step wizard (pick an opportunity -> project details ->
// generate & review). State/handlers live in useSowDraft(); this file is
// composition + the step indicator only -- see OpportunityStep.jsx,
// ProjectDetailsStep.jsx, DraftReviewStep.jsx, and the four modals for
// the rest.

import { Alert, PageHeader } from "../../shared/ui";
import { useSowDraft } from "./useSowDraft";
import OpportunityStep from "./OpportunityStep";
import ProjectDetailsStep from "./ProjectDetailsStep";
import DraftReviewStep from "./DraftReviewStep";
import PreviewModal from "./PreviewModal";
import RegenerateSectionModal from "./RegenerateSectionModal";
import VersionHistoryModal from "./VersionHistoryModal";
import SalesforcePushModal from "./SalesforcePushModal";
import RegisterProjectModal from "../../accounts/components/RegisterProjectModal";

const STEPS = [
  { num: 1, label: "Opportunity" },
  { num: 2, label: "Details" },
  { num: 3, label: "Draft" },
];

function StepIndicator({ step }) {
  return (
    <div className="flex items-center gap-2">
      {STEPS.map((s, idx) => (
        <div key={s.num} className="flex items-center gap-2">
          <div
            className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold ${
              step === s.num
                ? "bg-brand-600 text-white"
                : step > s.num
                ? "bg-green-100 text-green-700"
                : "bg-gray-100 text-muted"
            }`}
          >
            <span>{s.num}</span>
            <span>{s.label}</span>
          </div>
          {idx < STEPS.length - 1 && <span className="text-muted">›</span>}
        </div>
      ))}
    </div>
  );
}

export default function SowDraftPage() {
  const h = useSowDraft();

  const subtitle =
    h.step === 1
      ? "Select an opportunity to draft a SOW for."
      : h.step === 2
      ? `Fill in project details for ${h.selectedOpp?.account_name || ""}.`
      : "Review, edit, and publish your SOW.";

  return (
    <div>
      <PageHeader title="SOW Drafting" subtitle={subtitle} actions={<StepIndicator step={h.step} />} />

      {h.msg.text && (
        <Alert variant={h.msg.type === "error" ? "danger" : "success"} className="mb-4">
          {h.msg.text}
        </Alert>
      )}

      {h.step === 1 && (
        <OpportunityStep
          opps={h.opps}
          filteredOpps={h.filteredOpps}
          loading={h.oppLoading}
          search={h.oppSearch}
          onSearchChange={h.setOppSearch}
          draftsMap={h.draftsMap}
          onSelect={h.selectOpportunity}
          onLoadExisting={h.loadExistingDraft}
        />
      )}

      {h.step === 2 && h.selectedOpp && (
        <ProjectDetailsStep
          selectedOpp={h.selectedOpp}
          contractType={h.contractType}
          templates={h.templates}
          selectedTemplateId={h.selectedTemplateId}
          onTemplateChange={h.setSelectedTemplateId}
          projectDetails={h.projectDetails}
          onFieldChange={(field, value) => h.setProjectDetails((p) => ({ ...p, [field]: value }))}
          resourceTotals={h.resourceTotals}
          milestoneTotals={h.milestoneTotals}
          grandTotal={h.grandTotal}
          onAddMilestone={h.addMilestone}
          onUpdateMilestone={h.updateMilestone}
          onRemoveMilestone={h.removeMilestone}
          onAddResource={h.addResource}
          onUpdateResource={h.updateResource}
          onRemoveResource={h.removeResource}
          jossLoading={h.jossLoading}
          onFetchJoss={h.fetchJossResources}
          proposal={{
            files: h.proposalFiles,
            onFilesChange: h.handleProposalFileSelect,
            parsed: h.proposalParsed,
            uploading: h.proposalUploading,
            error: h.proposalError,
            onDismissError: () => h.setProposalError(""),
            onParse: h.uploadAndParseProposals,
            acceptedTypes: h.ACCEPTED_PROPOSAL_TYPES,
          }}
          generating={h.generating}
          genProgress={h.genProgress}
          onGenerate={h.createAndGenerate}
          onBack={h.goBack}
          canCreateProject={h.canCreateProject}
          onOpenNewProject={() => h.setNewProjectModalOpen(true)}
        />
      )}

      {h.step === 3 && h.draft && (
        <DraftReviewStep
          draft={h.draft}
          currentVersion={h.currentVersion}
          orderedSections={h.orderedSections}
          selectedSectionId={h.selectedSectionId}
          onSelectSection={h.setSelectedSectionId}
          selectedSection={h.selectedSection}
          onContentChange={h.updateContent}
          onSave={h.saveSection}
          onRegenerate={() => {
            h.setRegenInstructions("");
            h.setRegenModalOpen(true);
          }}
          busy={h.busy}
          generating={h.generating}
          genProgress={h.genProgress}
          onBack={h.goBack}
          onOpenVersions={() => h.setVersionModalOpen(true)}
          onOpenPreview={h.openPreview}
          onExportDocx={h.exportDocx}
          publishingGdoc={h.publishingGdoc}
          onPublishGdoc={h.publishToGoogleDocs}
          gdocLink={h.gdocLink}
          onOpenSalesforce={() => h.setSfModalOpen(true)}
        />
      )}

      <PreviewModal open={h.previewOpen} onClose={() => h.setPreviewOpen(false)} html={h.previewHtml} />

      <RegenerateSectionModal
        open={h.regenModalOpen}
        onClose={() => h.setRegenModalOpen(false)}
        sectionTitle={h.selectedSection?.title}
        instructions={h.regenInstructions}
        onInstructionsChange={h.setRegenInstructions}
        onConfirm={h.regenerateSection}
      />

      <VersionHistoryModal
        open={h.versionModalOpen}
        onClose={() => h.setVersionModalOpen(false)}
        currentVersion={h.currentVersion}
        onFetchVersions={h.fetchVersions}
        onRestore={h.restoreVersion}
        onFetchVersionDetail={h.fetchVersionDetail}
      />

      <SalesforcePushModal
        open={h.sfModalOpen}
        onClose={() => h.setSfModalOpen(false)}
        currentVersion={h.currentVersion}
        sfOpportunityId={h.selectedOpp?.sf_opportunity_id}
        onPush={h.pushToSalesforce}
      />

      <RegisterProjectModal
        open={h.newProjectModalOpen}
        accountName={h.currentAccount?.name}
        presetContractType={h.selectedOpp ? h.contractType : undefined}
        presetOpportunityId={h.selectedOpp?.id}
        onClose={() => h.setNewProjectModalOpen(false)}
        onRegister={h.registerProject}
      />
    </div>
  );
}
